import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SignIn, UserPlus, MagnifyingGlass, ArrowRight, Storefront } from '@phosphor-icons/react';

/* ═══════════════════════════════════════════════════
   Recent-business helpers (exported for Login pages)
   ═══════════════════════════════════════════════════ */
const KEY = 'pwa-recent-businesses';

export const addRecentBusiness = (slug, name, meta = {}) => {
  if (!slug) return;
  try {
    const stored = JSON.parse(localStorage.getItem(KEY) || '[]');
    const filtered = stored.filter(b => b.slug !== slug);
    filtered.unshift({
      slug,
      name: name || slug,
      type: meta.businessType || null,
      role: meta.role || null,
      lastVisited: Date.now(),
    });
    localStorage.setItem(KEY, JSON.stringify(filtered.slice(0, 5)));
  } catch { /* */ }
};

const load = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); }
  catch { return []; }
};

const remove = (slug) => {
  try {
    const s = JSON.parse(localStorage.getItem(KEY) || '[]');
    localStorage.setItem(KEY, JSON.stringify(s.filter(b => b.slug !== slug)));
  } catch { /* */ }
};

/* ═══════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════ */
const COLORS = ['#B8860B','#2563EB','#D97706','#7C3AED','#E11D48','#0891B2'];
const color = (n) => { let h=0; for(let i=0;i<n.length;i++) h=n.charCodeAt(i)+((h<<5)-h); return COLORS[Math.abs(h)%COLORS.length]; };

const greet = () => { const h=new Date().getHours(); return h<12?'Good morning':h<17?'Good afternoon':'Good evening'; };

const ago = (ts) => {
  const m=Math.floor((Date.now()-ts)/60000);
  if(m<1) return 'Just now'; if(m<60) return `${m}m ago`;
  const h=Math.floor(m/60); if(h<24) return `${h}h ago`;
  const d=Math.floor(h/24); return d<7?`${d}d ago`:new Date(ts).toLocaleDateString();
};

const typeLabel = (t) => {
  if(!t) return 'Business';
  const map = { RETAIL:'Retail Store', RESTAURANT:'Restaurant', SALON:'Salon', SERVICES:'Services', PHARMACY:'Pharmacy', SUPERMARKET:'Supermarket' };
  return map[t] || t.charAt(0)+t.slice(1).toLowerCase();
};

/* ═══════════════════════════════════════════════════
   African pattern – inline SVG (adinkra-inspired)
   ═══════════════════════════════════════════════════ */
const AfricanPattern = ({ color: c = 'rgba(255,255,255,0.06)', className = '' }) => (
  <svg className={className} width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style={{ position:'absolute', inset:0, pointerEvents:'none' }}>
    <defs>
      <pattern id="adinkra" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
        {/* Diamond grid */}
        <path d="M30 0 L60 30 L30 60 L0 30Z" fill="none" stroke={c} strokeWidth="1"/>
        {/* Inner diamond */}
        <path d="M30 10 L50 30 L30 50 L10 30Z" fill="none" stroke={c} strokeWidth="0.8"/>
        {/* Center cross */}
        <line x1="30" y1="20" x2="30" y2="40" stroke={c} strokeWidth="0.8"/>
        <line x1="20" y1="30" x2="40" y2="30" stroke={c} strokeWidth="0.8"/>
        {/* Corner dots */}
        <circle cx="30" cy="0" r="2" fill={c}/>
        <circle cx="60" cy="30" r="2" fill={c}/>
        <circle cx="30" cy="60" r="2" fill={c}/>
        <circle cx="0" cy="30" r="2" fill={c}/>
        {/* Small triangles at edges */}
        <path d="M25 5 L30 0 L35 5" fill="none" stroke={c} strokeWidth="0.6"/>
        <path d="M55 25 L60 30 L55 35" fill="none" stroke={c} strokeWidth="0.6"/>
        <path d="M25 55 L30 60 L35 55" fill="none" stroke={c} strokeWidth="0.6"/>
        <path d="M5 25 L0 30 L5 35" fill="none" stroke={c} strokeWidth="0.6"/>
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#adinkra)"/>
  </svg>
);

/* Africa continent silhouette */
const AfricaSilhouette = ({ size = 80, color: c = 'rgba(0,0,0,0.04)' }) => (
  <svg width={size} height={size} viewBox="0 0 100 120" fill={c} xmlns="http://www.w3.org/2000/svg">
    <path d="M55 2C52 1 48 2 45 4C42 6 38 8 36 12C34 16 30 18 28 22C25 27 22 30 20 35C18 40 17 45 16 50C15 55 14 58 15 63C16 68 18 72 20 76C22 80 25 84 28 88C31 92 34 96 38 99C42 102 45 104 48 108C50 111 52 114 55 116C57 114 59 111 60 108C62 104 64 102 66 99C69 96 72 92 74 88C76 84 78 80 80 76C82 72 83 68 84 63C85 58 84 55 83 50C82 45 80 40 78 35C76 30 73 27 70 22C68 18 65 16 63 12C61 8 58 6 55 4Z"/>
  </svg>
);


/* ═══════════════════════════════════════════════════
   PWAHome Component
   ═══════════════════════════════════════════════════ */
const PWAHome = () => {
  const [businesses, setBusinesses] = useState([]);
  const [removing, setRemoving] = useState(null);
  const nav = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => { setBusinesses(load()); }, []);

  // Track which tenant we want to navigate to after logout completes
  const [pendingSlug, setPendingSlug] = useState(null);

  // Navigate to login after auth state has cleared
  useEffect(() => {
    if (pendingSlug && !user) {
      nav(`/${pendingSlug}/login`);
      setPendingSlug(null);
    }
  }, [pendingSlug, user, nav]);

  const handleEnterBusiness = (slug) => {
    if (user?.tenantSlug === slug) {
      // Same tenant – go straight to dashboard
      nav(`/${slug}/dashboard`);
    } else if (user) {
      // Different tenant – logout first, navigate after state clears
      setPendingSlug(slug);
      logout();
    } else {
      // Not logged in – go straight to login
      nav(`/${slug}/login`);
    }
  };

  const handleRemove = (e, slug) => {
    e.stopPropagation();
    setRemoving(slug);
    setTimeout(() => {
      remove(slug);
      setBusinesses(p => p.filter(b => b.slug !== slug));
      setRemoving(null);
    }, 300);
  };

  const hasRecent = businesses.length > 0;

  return (
    <div style={{
      height: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      background: '#F7F7F5',
      overscrollBehavior: 'none',
      WebkitTapHighlightColor: 'transparent',
      userSelect: 'none',
    }}>

      {/* ── DARK HEADER with African pattern ── */}
      <div style={{
        flexShrink: 0,
        background: '#2D2A26',
        position: 'relative',
        overflow: 'hidden',
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}>
        <AfricanPattern color="rgba(255,255,255,0.05)" />

        <div style={{ position:'relative', zIndex:1, padding:'40px 28px 36px' }}>
          <img
            src="/logo192.png"
            alt=""
            style={{
              width: 48,
              height: 48,
              borderRadius: 16,
              marginBottom: 28,
              boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
              background: '#fff',
              padding: 4,
            }}
          />
          <p style={{ color:'rgba(255,255,255,0.4)', fontSize:14, fontWeight:500, marginBottom:6 }}>
            {greet()}
          </p>
          <h1 style={{
            color:'#fff', fontSize:30, fontWeight:800,
            letterSpacing:'-0.025em', lineHeight:1.12, margin:0,
          }}>
            {hasRecent ? <>Pick up where<br/>you left off.</> : <>Let's get you<br/>started.</>}
          </h1>
        </div>
      </div>

      {/* ── WHITE SHEET with pattern bleed ── */}
      <div style={{
        flex: 1,
        background: '#F7F7F5',
        borderTopLeftRadius: 28,
        borderTopRightRadius: 28,
        marginTop: -16,
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 -2px 20px rgba(0,0,0,0.08)',
      }}>
        {/* Subtle pattern on the right side */}
        <div style={{ position:'absolute', right:0, top:0, bottom:0, width:'50%', overflow:'hidden', pointerEvents:'none' }}>
          <AfricanPattern color="rgba(45,42,38,0.03)" />
        </div>

        {/* Scrollable content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          padding: '28px 20px 0',
          position: 'relative',
          zIndex: 1,
        }}>
          {hasRecent ? (
            <>
              <p style={{
                fontSize:11, fontWeight:700, color:'#9B9792',
                textTransform:'uppercase', letterSpacing:'0.08em',
                marginBottom:16, paddingLeft:2,
              }}>
                Your businesses
              </p>

              {businesses.map((biz) => {
                const c = color(biz.name);
                const isRemoving = removing === biz.slug;
                return (
                  <div
                    key={biz.slug}
                    style={{
                      marginBottom: 12,
                      opacity: isRemoving ? 0 : 1,
                      transform: isRemoving ? 'scale(0.95) translateX(40px)' : 'none',
                      transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                    }}
                  >
                    <button
                      onClick={() => handleEnterBusiness(biz.slug)}
                      style={{
                        width: '100%',
                        display: 'block',
                        padding: 0,
                        background: '#fff',
                        borderRadius: 20,
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                        overflow: 'hidden',
                        position: 'relative',
                        boxShadow: '0 2px 12px rgba(57,55,51,0.08), 0 4px 14px rgba(57,55,51,0.06), 0 0 0 1px rgba(0,0,0,0.03)',
                        transition: 'transform 0.2s cubic-bezier(0.16,1,0.3,1), box-shadow 0.2s ease',
                      }}
                      onPointerDown={(e) => { e.currentTarget.style.transform='scale(0.98)'; e.currentTarget.style.boxShadow='0 1px 4px rgba(57,55,51,0.10)'; }}
                      onPointerUp={(e) => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 4px 16px rgba(57,55,51,0.10), 0 8px 24px rgba(57,55,51,0.06), 0 0 0 1px rgba(0,0,0,0.03)'; setTimeout(() => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='0 2px 12px rgba(57,55,51,0.08), 0 4px 14px rgba(57,55,51,0.06), 0 0 0 1px rgba(0,0,0,0.03)'; }, 150); }}
                      onPointerLeave={(e) => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='0 2px 12px rgba(57,55,51,0.08), 0 4px 14px rgba(57,55,51,0.06), 0 0 0 1px rgba(0,0,0,0.03)'; }}
                    >
                      {/* Africa silhouette watermark */}
                      <div style={{ position:'absolute', right:12, top:8, opacity:0.5 }}>
                        <AfricaSilhouette size={64} color="rgba(45,42,38,0.04)" />
                      </div>

                      {/* Main row */}
                      <div style={{ display:'flex', alignItems:'center', gap:14, padding:'16px 18px 12px' }}>
                        {/* Avatar */}
                        <div style={{
                          width:50, height:50, borderRadius:16,
                          background:c, display:'flex',
                          alignItems:'center', justifyContent:'center', flexShrink:0,
                          boxShadow:`0 2px 8px ${c}40`,
                        }}>
                          <span style={{ color:'#fff', fontWeight:700, fontSize:20 }}>
                            {biz.name.charAt(0).toUpperCase()}
                          </span>
                        </div>

                        {/* Name + meta */}
                        <div style={{ flex:1, minWidth:0 }}>
                          <p style={{
                            fontSize:16, fontWeight:700, color:'#1A1917', margin:0,
                            whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                          }}>
                            {biz.name}
                          </p>
                          <p style={{ fontSize:12, color:'#A9A5A0', margin:'3px 0 0', fontWeight:500 }}>
                            {ago(biz.lastVisited)}
                          </p>
                        </div>
                      </div>

                      {/* Bottom bar: type + enter button */}
                      <div style={{
                        display:'flex', alignItems:'center', justifyContent:'space-between',
                        padding:'0 18px 14px',
                      }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <Storefront weight="fill" style={{ width:14, height:14, color:'#B5B1AB' }} />
                          <span style={{ fontSize:12, color:'#B5B1AB', fontWeight:600 }}>
                            {typeLabel(biz.type)}
                          </span>
                          {biz.role && (
                            <>
                              <span style={{ color:'#D5D1CC', margin:'0 2px' }}>·</span>
                              <span style={{ fontSize:11, color:'#B5B1AB', fontWeight:500 }}>
                                {biz.role === 'OWNER' || biz.role === 'ADMIN' ? 'Owner' :
                                 biz.role === 'MANAGER' ? 'Manager' :
                                 biz.role === 'CASHIER' ? 'Cashier' :
                                 biz.role === 'KITCHEN' ? 'Kitchen' : biz.role}
                              </span>
                            </>
                          )}
                        </div>

                        <div style={{
                          display:'flex', alignItems:'center', gap:6,
                          background:'linear-gradient(135deg, #C9960C 0%, #B8860B 100%)', borderRadius:10,
                          padding:'7px 14px', color:'#fff',
                          boxShadow:'0 2px 8px rgba(201,150,12,0.30)',
                          fontSize:13, fontWeight:600,
                        }}>
                          enter
                          <ArrowRight weight="bold" style={{ width:14, height:14 }} />
                        </div>
                      </div>
                    </button>

                    {/* Remove link */}
                    <div style={{ textAlign:'right', paddingRight:6, marginTop:4 }}>
                      <button
                        onClick={(e) => handleRemove(e, biz.slug)}
                        style={{
                          fontSize:11, color:'#D5D1CC', background:'none', border:'none',
                          cursor:'pointer', padding:'2px 8px', borderRadius:6, fontWeight:500,
                          transition:'color 0.15s',
                        }}
                        onMouseOver={(e) => e.currentTarget.style.color='#E11D48'}
                        onMouseOut={(e) => e.currentTarget.style.color='#D5D1CC'}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            /* Empty state */
            <div style={{
              display:'flex', flexDirection:'column', alignItems:'center',
              justifyContent:'center', paddingTop:56, paddingBottom:56, textAlign:'center',
            }}>
              <div style={{
                width:88, height:88, borderRadius:28, background:'rgba(45,42,38,0.05)',
                display:'flex', alignItems:'center', justifyContent:'center', marginBottom:24,
                position:'relative',
              }}>
                <AfricaSilhouette size={48} color="rgba(45,42,38,0.12)" />
              </div>
              <p style={{ fontSize:22, fontWeight:700, color:'#2D2A26', margin:'0 0 8px', letterSpacing:'-0.01em' }}>
                Welcome to SmartPOS
              </p>
              <p style={{ fontSize:14, color:'#A9A5A0', lineHeight:1.6, maxWidth:240, margin:0 }}>
                Sign in to your business or create a new one to get started.
              </p>
            </div>
          )}
        </div>

        {/* ── BOTTOM ACTIONS ── */}
        <div style={{
          flexShrink: 0,
          padding: '12px 20px',
          paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 16px))',
          position: 'relative',
          zIndex: 1,
        }}>
          {/* Log in – primary */}
          <button
            onClick={() => nav('/login')}
            style={{
              width:'100%', height:54, borderRadius:16,
              background:'#1A1917', color:'#fff',
              fontSize:16, fontWeight:700, border:'none',
              cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:10,
              marginBottom:10, transition:'transform 0.1s', letterSpacing:'-0.01em',
              boxShadow:'0 2px 12px rgba(26,25,23,0.20)',
            }}
            onPointerDown={(e) => e.currentTarget.style.transform='scale(0.97)'}
            onPointerUp={(e) => e.currentTarget.style.transform='none'}
            onPointerLeave={(e) => e.currentTarget.style.transform='none'}
          >
            <SignIn weight="bold" style={{ width:20, height:20 }} />
            Log in
          </button>

          {/* Find + Sign up – side by side */}
          <div style={{ display:'flex', gap:10, marginBottom:8 }}>
            <button
              onClick={() => nav('/find-business')}
              style={{
                flex: 1, height:48, borderRadius:14,
                background:'#fff', color:'#1A1917',
                fontSize:14, fontWeight:600, border:'1px solid rgba(0,0,0,0.06)',
                cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                transition:'transform 0.1s', boxShadow:'0 1px 4px rgba(0,0,0,0.04)',
              }}
              onPointerDown={(e) => e.currentTarget.style.transform='scale(0.96)'}
              onPointerUp={(e) => e.currentTarget.style.transform='none'}
              onPointerLeave={(e) => e.currentTarget.style.transform='none'}
            >
              <MagnifyingGlass weight="bold" style={{ width:17, height:17 }} />
              Find
            </button>
            <button
              onClick={() => nav('/signup')}
              style={{
                flex: 1, height:48, borderRadius:14,
                background:'#B8860B', color:'#fff',
                fontSize:14, fontWeight:600, border:'none',
                cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                transition:'transform 0.1s', boxShadow:'0 2px 8px rgba(201,150,12,0.25)',
              }}
              onPointerDown={(e) => e.currentTarget.style.transform='scale(0.96)'}
              onPointerUp={(e) => e.currentTarget.style.transform='none'}
              onPointerLeave={(e) => e.currentTarget.style.transform='none'}
            >
              <UserPlus weight="bold" style={{ width:17, height:17 }} />
              Sign up
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PWAHome;
